<template>
  <div id="app">
    <router-view></router-view>

    <!-- Session-expired dialog (fired by address.js interceptor on HTTP 403) -->
    <transition name="session-fade">
      <div v-if="showSession403Dialog" class="session-overlay">
        <div class="session-modal" role="dialog" aria-modal="true">
          <div class="session-modal-icon">
            <icon-clock-timer :size="44" color="#F59E0B" />
          </div>
          <h3 class="session-modal-title">Session Expired</h3>
          <p class="session-modal-desc">
            Your session has expired. Click <strong>Continue</strong> to refresh your session and stay on this page, or <strong>Cancel</strong> to go to the login page.
          </p>
          <p v-if="session403Error" class="session-modal-error">{{ session403Error }}</p>
          <div class="session-modal-actions">
            <button class="session-btn session-btn--cancel" :disabled="session403Loading" @click="onSession403Cancel">
              Cancel
            </button>
            <button class="session-btn session-btn--continue" :disabled="session403Loading" @click="onSession403Continue">
              <span v-if="session403Loading" class="session-spinner"></span>
              <span>{{ session403Loading ? 'Refreshing…' : 'Continue' }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import tools from 'common/javascript/tools/tools'
import IconClockTimer from 'common/image/icons/IconClockTimer.vue'
import { getStoredTokens, refreshAccessToken, saveTokens } from 'common/api/auth'
import { sessionGuardState } from 'common/api/sessionGuard'

export default {
  name: 'app',
  components: { IconClockTimer },
  data () {
    return {
      session403Loading: false,
      session403Error: ''
    }
  },
  computed: {
    showSession403Dialog () {
      return sessionGuardState.active
    }
  },
  watch: {
    showSession403Dialog (val) {
      if (val) {
        this.session403Error = ''
        this.session403Loading = false
      }
    }
  },
  mounted () {
    var lang = tools.getLang()
    this.$store.commit('setActiveLang', lang)

    // Load Google Analytics based on environment variable
    function gtag () {
      window.dataLayer.push(arguments)
    }

    const gaTrackingId = process.env.GA_TRACKING_ID

    if (gaTrackingId) {
      const script = document.createElement('script')
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`
      script.async = true
      document.head.appendChild(script)

      window.dataLayer = window.dataLayer || []
      gtag('js', new Date())
      gtag('config', gaTrackingId)
    }
  },
  methods: {
    async onSession403Continue () {
      const { refreshToken } = getStoredTokens()
      if (!refreshToken) {
        this.onSession403Cancel()
        return
      }
      this.session403Loading = true
      this.session403Error = ''
      try {
        const data = await refreshAccessToken(refreshToken)
        // Mirror login: persist tokens in localStorage and Vuex
        saveTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken })
        this.$store.commit('setTokens', { accessToken: data.accessToken, refreshToken: data.refreshToken })
        if (data.user) this.$store.commit('setCurrentUser', data.user)
        // Resume the original frozen request with the new token
        sessionGuardState.handler.onContinue(data.accessToken)
      } catch (err) {
        const msg = (err && err.message) || ''
        if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('expired')) {
          this.session403Error = 'Refresh token is no longer valid. Redirecting to login…'
          setTimeout(() => sessionGuardState.handler && sessionGuardState.handler.onCancel(), 1800)
        } else {
          this.session403Error = 'Failed to refresh session. Please try again or cancel.'
        }
      } finally {
        this.session403Loading = false
      }
    },
    onSession403Cancel () {
      if (sessionGuardState.handler) sessionGuardState.handler.onCancel()
    }
  }
}
</script>

<style>
/* ── Session-expired dialog ───────────────────────────────────────────────── */
.session-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.session-modal {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  width: 400px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.session-modal-icon { margin-bottom: 4px; }

.session-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.session-modal-desc {
  margin: 0;
  font-size: 14px;
  color: #4B5563;
  line-height: 1.6;
}

.session-modal-error {
  margin: 0;
  font-size: 13px;
  color: #DC2626;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 8px 12px;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

.session-modal-actions {
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 4px;
}

.session-btn {
  flex: 1;
  height: 44px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.15s, opacity 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.session-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.session-btn--cancel { background: #F3F4F6; color: #374151; }
.session-btn--cancel:not(:disabled):hover { background: #E5E7EB; }

.session-btn--continue { background: #4D66F6; color: #fff; }
.session-btn--continue:not(:disabled):hover { background: #3A53E0; }

.session-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: session-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes session-spin { to { transform: rotate(360deg); } }

.session-fade-enter-active,
.session-fade-leave-active { transition: opacity 0.2s ease; }

.session-fade-enter,
.session-fade-leave-to { opacity: 0; }
</style>
